import React, { Component } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { confirmAction } from "../../utils/confirmHelpers";
import { ReviewRosulutionStatuses, PaymentRequestPendingReview } from "../../models/Orchestrator";
import { orchestratorApiService } from "../../services/api/Compliance/OrchestratorApiService";
import { toastService } from "../../services/toastService";

interface Props {
    paymentRequest: PaymentRequestPendingReview;
    onHide: () => void;
}

interface State {
    submitting: boolean;
    resolution: ReviewRosulutionStatuses | null;
    takingLong: boolean;
}

export default class ReviewPendingPaymentRequestModal extends Component<Props, State> {
    private slowSubmissionTimer: ReturnType<typeof setTimeout> | null = null;

    state: State = { submitting: false, resolution: null, takingLong: false };

    componentWillUnmount() {
        this.clearSlowSubmissionTimer();
    }

    private clearSlowSubmissionTimer = () => {
        if (this.slowSubmissionTimer) {
            clearTimeout(this.slowSubmissionTimer);
            this.slowSubmissionTimer = null;
        }
    };

    private finishSubmitting = () => {
        this.clearSlowSubmissionTimer();
        this.setState({ submitting: false, resolution: null, takingLong: false });
    };

    private failureMessage = (status?: number) => {
        if (status === undefined) {
            return "Unable to reach the review service. Check your connection and try again.";
        }

        if (status === 403) {
            return "You do not have permission to resolve this payment review.";
        }

        if (status === 408 || status === 504) {
            return "The review decision took too long. Check the queue before trying again.";
        }

        if (status >= 500) {
            return "The review service could not complete the decision. Please try again later.";
        }

        return "The review decision could not be completed. Please try again.";
    };

    resolve = (resolution: ReviewRosulutionStatuses) => {
        if (this.state.submitting) {
            return;
        }

        const label = resolution === ReviewRosulutionStatuses.Approve ? "approve" : "reject";
        confirmAction(`Are you sure you want to ${label} this Visa Direct payment?`, "Confirm review", async () => {
            this.setState({ submitting: true, resolution, takingLong: false });
            this.slowSubmissionTimer = setTimeout(() => this.setState({ takingLong: true }), 3000);

            const result = await orchestratorApiService.resolvePaymentReview(this.props.paymentRequest.paymentRequestId, { reviewResolution: resolution });
            this.finishSubmitting();
            if (!result.success) {
                if (result.status === 409) {
                    toastService.error("Another officer has already resolved this payment. The queue has been refreshed.");
                    this.props.onHide();
                    return;
                }

                toastService.error(this.failureMessage(result.status));
                return;
            }

            toastService.success(result.data.message);
            this.props.onHide();
        });
    };

    render() {
        const { paymentRequest } = this.props;
        const { resolution, submitting, takingLong } = this.state;
        return (
            <Dialog visible modal closable={!submitting} header="Visa Direct Compliance Review" style={{ width: "42rem" }} onHide={this.props.onHide}>
                <div className="flex flex-column gap-3">
                    <div><strong>Payment request:</strong> {paymentRequest.paymentRequestId}</div>
                    <div><strong>Customer:</strong> {paymentRequest.senderName || "-"}</div>
                    <div><strong>Beneficiary:</strong> {paymentRequest.beneficiaryName || "-"}</div>
                    <div><strong>Amount:</strong> {paymentRequest.amount} {paymentRequest.currency}</div>
                    <div><strong>Route:</strong> {paymentRequest.routeId || "-"}</div>
                    <div><strong>Risk reason:</strong> {[paymentRequest.riskDecisionFamily, paymentRequest.riskReasonCode, paymentRequest.riskReasonText].filter(Boolean).join(" — ") || "-"}</div>
                    {submitting && (
                        <div role="status">
                            {takingLong
                                ? "The decision is taking longer than expected. Please keep this window open while it completes."
                                : `Submitting ${resolution === ReviewRosulutionStatuses.Approve ? "approval" : "rejection"}…`}
                        </div>
                    )}
                    <div className="flex justify-content-end gap-2">
                        <Button label="Reject" severity="danger" disabled={submitting} loading={submitting && resolution === ReviewRosulutionStatuses.Reject} onClick={() => this.resolve(ReviewRosulutionStatuses.Reject)} />
                        <Button label="Approve" severity="success" disabled={submitting} loading={submitting && resolution === ReviewRosulutionStatuses.Approve} onClick={() => this.resolve(ReviewRosulutionStatuses.Approve)} />
                    </div>
                </div>
            </Dialog>
        );
    }
}
